import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader, DataTable } from '../../components/office';
import { Button } from '@verlo/shared/components/ui/button';
import { Signal } from '@verlo/shared/components/ui/signal';
import { Segmented } from '@verlo/shared/components/ui/segmented';
import { promotionsApi } from '@verlo/shared/services/api';
import { scheduleWords } from '@verlo/shared/lib/promotions';
import { listOf, rightNow, venueNow } from '../../lib/promoLists';
import { toast } from 'sonner';

/**
 * Menu › Promotions (Back office sweep, Oct 2026: D9, mock "Promotions"). Four lists, each
 * with its count: Live (inside its dates; "On now" when it's actually running), Scheduled
 * (starts on a later date), Drafts and Ended. A row says what the offer does in plain words,
 * when it runs, what it's doing right now, and how it went over the last 30 days. A
 * promotion opens on its own page; pausing and ending are there.
 */
const LISTS = [
  { value: 'live', label: 'Live' }, { value: 'scheduled', label: 'Scheduled' },
  { value: 'drafts', label: 'Drafts' }, { value: 'ended', label: 'Ended' },
];

const fmtDate = (d) => {
  try { return new Date(`${d}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }); } catch { return d; }
};
const whenWords = (p) => {
  const s = p.schedule || {};
  const w = scheduleWords(s);
  const dates = s.start && s.end ? `${fmtDate(s.start)} to ${fmtDate(s.end)}` : s.start ? `from ${fmtDate(s.start)}` : s.end ? `until ${fmtDate(s.end)}` : '';
  const out = [w || 'every day, all day', dates].filter(Boolean).join(', ');
  return out[0].toUpperCase() + out.slice(1);
};
const money = (v) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(v || 0);

const PromotionsPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [running, setRunning] = useState(new Set());
  const [usage, setUsage] = useState(null);       // null: not shown (no access to the reports)
  const [clock, setClock] = useState(null);       // the venue's trading day, timezone and day start
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState('live');
  const now = venueNow(clock?.tz);
  const today = clock?.today || `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  useEffect(() => {
    Promise.all([
      promotionsApi.list(),
      promotionsApi.live().catch(() => ({ data: { promotions: [] } })),
      promotionsApi.usage({ days: 30 }).catch(() => ({ data: null })),
    ])
      .then(([p, l, u]) => {
        setRows(p.data || []);
        setRunning(new Set((l.data?.promotions || []).map((x) => x.id)));
        setClock(u.data ? { today: u.data.today, tz: u.data.tz, dayStart: u.data.day_start } : null);
        setUsage(u.data?.usage || null);
      })
      .catch(() => toast.error("Couldn't load the promotions"))
      .finally(() => setLoading(false));
  }, []);

  const counts = useMemo(() => {
    const c = { live: 0, scheduled: 0, drafts: 0, ended: 0 };
    rows.forEach((r) => { c[listOf(r, today)] += 1; });
    return c;
  }, [rows, today]);
  const shown = rows.filter((r) => listOf(r, today) === list);

  const columns = [
    { key: 'name', label: 'Promotion', sortable: true, render: (r) => (
      <div className="min-w-0">
        <p className={`font-semibold ${list === 'ended' ? 'text-ink-faint' : 'text-ink'}`}>{r.name}</p>
        <p className="truncate text-[12.5px] text-ink-soft">{r.words}</p>
      </div>
    ) },
    { key: 'when', label: 'When', render: (r) => <span className="text-ink-soft">{whenWords(r)}</span> },
    { key: 'now', label: 'Right now', render: (r) => {
      if (list === 'ended') return <span className="text-ink-faint">Ended</span>;
      if (list === 'drafts') return <span className="text-ink-faint">Not live</span>;
      if (list === 'scheduled') return <span className="text-ink-soft">Starts {fmtDate(r.schedule?.start)}</span>;
      const rn = rightNow(r, now, running, clock?.dayStart);
      return rn.tone ? <Signal tone={rn.tone}>{rn.words}</Signal> : <span className="text-ink-soft">{rn.words}</span>;
    } },
    // Only for someone who can see the reports; anyone else doesn't get the column.
    ...(usage ? [{ key: 'usage', label: 'Last 30 days', align: 'right', render: (r) => {
      const u = usage[r.id];
      return u && u.redemptions ? <span className="tabular-nums text-ink-soft">{u.redemptions} time{u.redemptions === 1 ? '' : 's'} · {money(u.given)} off</span> : <span className="text-ink-faint">Not used</span>;
    } }] : []),
  ];

  return (
    <div data-testid="promotions-page">
      <PageHeader
        title="Promotions"
        purpose="Offers the till applies by itself, on the days and times you set."
        actions={<Button className="bg-verlo hover:bg-verlo-hover" onClick={() => navigate('/back-office/promotions/new')} data-testid="promotion-add">New promotion</Button>}
      />
      <div className="mb-4">
        <Segmented value={list} onChange={setList}
          options={LISTS.map((l) => ({ value: l.value, label: `${l.label} ${counts[l.value]}`, testId: `promotions-list-${l.value}` }))} />
      </div>
      <DataTable
        columns={columns} rows={shown} rowKey="id" loading={loading}
        onRowClick={(r) => navigate(`/back-office/promotions/${r.id}`)}
        empty={{
          word: 'Promotions',
          title: { live: 'Nothing live', scheduled: 'Nothing scheduled', drafts: 'No drafts', ended: 'Nothing has ended' }[list],
          action: list === 'live' ? <Button variant="outline" onClick={() => navigate('/back-office/promotions/new')}>Set one up</Button> : undefined,
          children: list === 'live' ? 'A happy hour, a free kids’ main, a supplier deal: set it up once and the till does the rest.' : undefined,
        }}
        testId="promotions-table"
      />
    </div>
  );
};

export default PromotionsPage;
